'use client';

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import {
  fetchMobileUiConfig,
  mobileKeys,
  type MobileUiConfigArtifact,
  type MobileUiConfigResponse,
} from '@/shared/mobile-service/api/cors-mobile-api';

/**
 * Fetch and cache the mobile-service UI-config document once per session.
 *
 * The document is static per deployment (schema + curated ``front_end`` blocks),
 * so it is cached aggressively and reused by every artifact table. Consumers
 * look up their ``graphqlType`` via {@link useMobileUiConfigArtifact}.
 */
export function useMobileUiConfig(
  featureEnabled = true,
): UseQueryResult<MobileUiConfigResponse, Error> {
  const { cellId, accessToken, enabled: sessionEnabled } = useCellServiceSession();

  return useQuery<MobileUiConfigResponse, Error>({
    queryKey: mobileKeys.uiConfig(cellId),
    enabled: featureEnabled && sessionEnabled,
    // UI config rarely changes within a session; keep it effectively immutable.
    staleTime: Infinity,
    gcTime: Infinity,
    queryFn: ({ signal }) =>
      fetchMobileUiConfig({ cellId: cellId as string, accessToken: accessToken as string, signal }),
  });
}

/**
 * Convenience selector: the UI-config entry for one GraphQL type, or
 * ``undefined`` while loading / when the type is unknown.
 */
export function useMobileUiConfigArtifact(
  graphqlType: string | null,
  enabled = true,
): { artifact: MobileUiConfigArtifact | undefined; isLoading: boolean; error: Error | null } {
  const { data, isLoading, error } = useMobileUiConfig(enabled);
  const artifact =
    graphqlType && data ? data.artifacts[graphqlType] : undefined;
  return { artifact, isLoading, error };
}
